/**
 * SplitChoice Component
 *
 * This component provides a language selection dropdown and a split choice UI for selecting between light and dark modes.
 * It fetches available languages using the useLanguageResource hook and updates the user's language preference.
 *
 * Features:
 * - Language selection with flag icons
 * - Swipeable panels for light/dark mode selection
 * - User verification check for personalized greeting
 * - Responsive and accessible UI
 *
 * State:
 * - languageOption: List of available languages for the dropdown
 * - isUserVerified: Whether the user is verified (has a phone number)
 * - selectedMode: Currently selected mode ('light', 'dark', or null)
 *
 * Context:
 * - Uses LocaleContext for selectedLocale and setSelectedLocale
 *
 * Usage:
 * This component is rendered at the '/choose' route and allows users to select their preferred language and mode.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import Dropdown from "../components/FormElements/DropDown";
import LightChoice from "../components/SplitChoice/LightChoice";
import DarkChoice from "../components/SplitChoice/DarkChoice";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "../context/useLocale";
import { useUserApi } from "../hooks/useUserApi";
import useLanguageResource from "../resources/useLanguagesResource";

interface LanguageOption {
  /**
   * Display label for the language (e.g., 'English')
   */
  label: string;
  /**
   * Locale code for the language (e.g., 'en', 'hi')
   */
  value: string;
  /**
   * URL to the flag icon representing the language
   */
  icon: string;
}

interface Handle {
  user: {
    /**
     * User's preferred language code
     */
    language_code: string;
    /**
     * User's phone number (if verified)
     */
    phone_number?: string;
  };
}

// Default English locale fallback
const defaultEnglishLocale: LanguageOption = {
  label: "English",
  value: "en",
  icon: "https://flagcdn.com/16x12/us.png",
};

const SplitChoice: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [languageOption, setLanguageOption] = useState<LanguageOption[]>([]);
  const rawHandle = localStorage.getItem("handle");
  const handle: Handle | null = rawHandle ? JSON.parse(rawHandle) : null;
  const { selectedLocale, setSelectedLocale } = useLocale();

  const [isUserVerified, setIsUserVerified] = useState<boolean>(false);
  const [selectedMode, setSelectedMode] = useState<"light" | "dark" | null>(null);
  const { updateUser } = useUserApi();
  const { getLanguages } = useLanguageResource();

  // Swipe handlers
  const handlers = useSwipeable({
    onSwipedLeft: () => setSelectedMode("dark"),
    onSwipedRight: () => setSelectedMode("light"),
    delta: 50, // Minimum swipe distance
    preventScrollOnSwipe: true,
  });

  const hasFetchedLanguages = useRef(false);

  /**
   * Fetches available languages and sets the default selected language based on user or browser preference.
   */
  const fetchLanguages = useCallback(async () => {
    try {
      const res = await getLanguages();
      const lan: LanguageOption[] = res.map((data) => ({
        label: data.name,
        value: data.locale,
        icon: `https://flagcdn.com/16x12/${data.country_code}.png`,
      }));
      setLanguageOption(lan);
      let selectedLang: string;
      if (handle) {
        selectedLang = handle.user.language_code.toLowerCase();
      } else {
        selectedLang = navigator.language.split("-")[0];
      }
      setSelectedLocale(
        lan.find((item) => item.value.toLowerCase() === selectedLang) || defaultEnglishLocale
      );
      i18n.changeLanguage(selectedLang);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [setLanguageOption, setSelectedLocale, handle, i18n, getLanguages]);

  /**
   * Handles language selection from the dropdown and updates user preference if logged in.
   * @param e The selected language option
   */
  const handleLanguageSelection = async (e: LanguageOption) => {
    const lang = e.value;
    if (handle) {
      await updateUser({ language_code: lang });
      setSelectedLocale(e);
    } else {
      try {
        setSelectedLocale(e);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    setSelectedLocale(e);
    i18n.changeLanguage(lang);
  };

  // Reset selectedMode after animation duration (2.5 seconds)
  useEffect(() => {
    if (selectedMode) {
      const timer = setTimeout(() => {
        setSelectedMode(null);
      }, 2500); // Match animation duration
      return () => clearTimeout(timer); // Cleanup on unmount or change
    }
  }, [selectedMode]);

  useEffect(() => {
    if (handle) {
      if (handle.user.phone_number) {
        setIsUserVerified(true);
      }
    }
    if (!hasFetchedLanguages.current) {
      fetchLanguages();
      hasFetchedLanguages.current = true;
    }
  }, [fetchLanguages, handle]);

  return (
    <div className="w-full md:w-[1024px] mx-auto h-dvh flex flex-col items-center overflow-hidden bg-bg-grey">
      {/* Language Dropdown */}
      <div className="px-3 mt-2  py-2 z-50 w-full max-w-[340px] flex justify-center ">
        <Dropdown
          options={languageOption}
          placeholder={t("selectLanguage")}
          value={selectedLocale}
          changeHandler={handleLanguageSelection}
        />
      </div>

      {/* Swipeable Panels */}
      <div className="flex flex-1 mt-2 w-full relative">
        <div className="flex gap-2 min-h-full w-full" {...handlers}>
          {/* Light Panel */}
          <div
            className={`flex-1 h-full flex items-center justify-center transition-opacity duration-500 flex-shrink-0 w-full overflow-hidden`}
          >
            <LightChoice isSelected={selectedMode === "light"} />
          </div>

          {/* Dark Panel */}
          <div
            className={`flex-1 h-full flex items-center justify-center text-white transition-opacity duration-500 flex-shrink-0 w-full overflow-hidden `}
          >
            <DarkChoice isSelected={selectedMode === "dark"} />
          </div>
        </div>
      </div>

      {/* Sign In Section */}
      <div className="mt-7 flex flex-col items-center pb-7 flex-shrink-0">
        {isUserVerified ? (
          <>
            <h6 className="font-bold text-20 text-sangati-green">
              {t("welcomeTitle")}
            </h6>
            <p className="font-bold text-12 text-sangati-green">
              {t("selectModePrompt")}
            </p>
          </>
        ) : (
          <>
            <span className="text-12 text-black">
              {t("alreadyHaveAccount")}
            </span>
            <Link
              to="/login"
              className="cursor-pointer -mt-1 text-20 text-sangati-orange font-bold underline"
            >
              {t("signInButton")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default SplitChoice;
